import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Radio,
  Plus,
  RefreshCw,
  Globe,
  DollarSign,
  Zap,
  Monitor,
  Link2,
  Settings,
  Play,
  TrendingUp,
  Copy,
  CheckCircle,
  ExternalLink,
} from 'lucide-react';

export default function ProgrammaticPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState(null);
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('inventory');
  const [formData, setFormData] = useState({
    name: '',
    type: 'generic',
    endpoint_url: '',
    api_key: '',
    is_active: true,
    settings: {},
  });

  const apiBaseUrl = window.location.origin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invRes, statsRes] = await Promise.all([
        api.get('/programmatic/inventory'),
        api.get('/programmatic/stats'),
      ]);
      setInventory(invRes.data);
      setStats(statsRes.data);
      
      if (isAdmin) {
        const connRes = await api.get('/programmatic/dsp-connections');
        setConnections(connRes.data);
      }
    } catch (error) {
      toast.error('Failed to load programmatic data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddConnection = async (e) => {
    e.preventDefault();
    try {
      await api.post('/programmatic/dsp-connections', formData);
      toast.success('DSP connection added');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to add connection');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="programmatic-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Programmatic Advertising
          </h1>
          <p className="text-zinc-400 mt-1">
            Connect with DSPs and sell inventory programmatically
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 btn-glow"
            data-testid="add-dsp-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add DSP Connection
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {inventory?.total_screens || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Available Screens</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {(inventory?.total_daily_impressions || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Daily Impressions</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${stats?.total_revenue?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Programmatic Revenue</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {stats?.fill_rate || '0%'}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Fill Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-blue-600">
            <Monitor className="w-4 h-4 mr-2" />
            Inventory
          </TabsTrigger>
          <TabsTrigger value="integration" className="data-[state=active]:bg-blue-600">
            <Link2 className="w-4 h-4 mr-2" />
            Integration
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="connections" className="data-[state=active]:bg-blue-600">
              <Settings className="w-4 h-4 mr-2" />
              DSP Connections
            </TabsTrigger>
          )}
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">Available Screen Inventory</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Screen</TableHead>
                    <TableHead className="text-zinc-500">Location</TableHead>
                    <TableHead className="text-zinc-500">Type</TableHead>
                    <TableHead className="text-zinc-500">Resolution</TableHead>
                    <TableHead className="text-zinc-500 text-right">Floor Price</TableHead>
                    <TableHead className="text-zinc-500 text-right">Daily Imps</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory?.inventory?.map((screen, index) => (
                    <TableRow key={index} className="border-zinc-800">
                      <TableCell className="text-white font-medium">{screen.screen_name}</TableCell>
                      <TableCell className="text-zinc-400">{screen.location}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                          {screen.display_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-zinc-400">{screen.resolution}</TableCell>
                      <TableCell className="text-emerald-400 text-right">${screen.floor_price}</TableCell>
                      <TableCell className="text-zinc-400 text-right">
                        {screen.available_impressions.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Tab */}
        <TabsContent value="integration" className="mt-6">
          <div className="space-y-6">
            {/* API Endpoints */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">OpenRTB 2.5 API Endpoints</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-zinc-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">POST</Badge>
                      <span className="text-white font-mono text-sm">Bid Request</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`${apiBaseUrl}/api/programmatic/bid-request`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <code className="text-zinc-400 text-sm">{apiBaseUrl}/api/programmatic/bid-request</code>
                  <p className="text-zinc-500 text-xs mt-2">Send OpenRTB 2.5 bid requests to this endpoint</p>
                </div>

                <div className="p-4 rounded-lg bg-zinc-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">GET</Badge>
                      <span className="text-white font-mono text-sm">Inventory</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`${apiBaseUrl}/api/programmatic/inventory`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <code className="text-zinc-400 text-sm">{apiBaseUrl}/api/programmatic/inventory</code>
                  <p className="text-zinc-500 text-xs mt-2">Get available screen inventory and floor prices</p>
                </div>

                <div className="p-4 rounded-lg bg-zinc-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">POST</Badge>
                      <span className="text-white font-mono text-sm">Win Notice</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`${apiBaseUrl}/api/programmatic/win-notice`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <code className="text-zinc-400 text-sm">{apiBaseUrl}/api/programmatic/win-notice</code>
                  <p className="text-zinc-500 text-xs mt-2">Notify of winning bids for impression tracking</p>
                </div>
              </CardContent>
            </Card>

            {/* Supported DSPs */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">Supported DSP Platforms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { name: 'LemmaTV', status: 'Compatible', icon: Globe },
                    { name: 'DV360', status: 'Compatible', icon: Globe },
                    { name: 'The Trade Desk', status: 'Compatible', icon: Globe },
                    { name: 'Generic OpenRTB', status: 'Ready', icon: Radio },
                  ].map((dsp, index) => (
                    <div key={index} className="p-4 rounded-lg bg-zinc-800/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <dsp.icon className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{dsp.name}</p>
                        <p className="text-xs text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {dsp.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Integration Guide */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">Quick Integration Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Configure DSP Endpoint</h4>
                    <p className="text-zinc-400 text-sm">
                      Point your DSP to our OpenRTB 2.5 bid request endpoint
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Set Floor Prices</h4>
                    <p className="text-zinc-400 text-sm">
                      Configure minimum bid prices per screen in screen settings
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Configure Win Notices</h4>
                    <p className="text-zinc-400 text-sm">
                      Set up win notice callbacks for accurate billing and tracking
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DSP Connections Tab */}
        {isAdmin && (
          <TabsContent value="connections" className="mt-6">
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-white">Active DSP Connections</CardTitle>
                  <Button variant="outline" size="sm" onClick={loadData} className="border-zinc-700">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {connections.length === 0 ? (
                  <div className="text-center py-12">
                    <Link2 className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No DSP connections</h3>
                    <p className="text-zinc-400 mb-4">Add your first DSP connection to start programmatic selling</p>
                    <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Connection
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-500">Name</TableHead>
                        <TableHead className="text-zinc-500">Type</TableHead>
                        <TableHead className="text-zinc-500">Endpoint</TableHead>
                        <TableHead className="text-zinc-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {connections.map((conn, index) => (
                        <TableRow key={index} className="border-zinc-800">
                          <TableCell className="text-white font-medium">{conn.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                              {conn.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-400 font-mono text-xs">
                            {conn.endpoint_url}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              conn.is_active
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                            }>
                              {conn.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add DSP Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Add DSP Connection
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddConnection} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Connection Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="My DSP Connection"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-300">DSP Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="generic">Generic OpenRTB</SelectItem>
                  <SelectItem value="lemma">LemmaTV</SelectItem>
                  <SelectItem value="dv360">DV360 (Google)</SelectItem>
                  <SelectItem value="tradedesk">The Trade Desk</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-300">Endpoint URL</Label>
              <Input
                value={formData.endpoint_url}
                onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="https://dsp.example.com/rtb"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-300">API Key (Optional)</Label>
              <Input
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Your API key"
                type="password"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-zinc-300">Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                Add Connection
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
